import { useState, type FormEvent } from "react";
import { Copy, KeyRound, Plus, RefreshCw, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { displayError, formatDate, formatNumber } from "../lib/format";
import { notify } from "../lib/notify";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorState, LoadingState, Modal, PageHeader, StatusBadge } from "../components/Ui";

const scopeOptions = ["videos:create", "videos:read"];

export default function ApiKeysPage() {
  const { data, loading, error, refresh } = useResource((signal) => api.apiKeys(signal), [], 30_000);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const keys = data?.items || [];

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    try {
      const result = await api.createApiKey({
        name: form.get("name"),
        scopes: scopeOptions.filter((scope) => form.getAll("scopes").includes(scope)),
        minute_limit: optionalNumber(form.get("rate_limit_per_minute")),
        daily_limit: optionalNumber(form.get("daily_quota")),
        monthly_limit: optionalNumber(form.get("monthly_quota")),
      });
      const key = String(result.key || result.api_key?.key || "");
      setCreateOpen(false);
      if (key) setRevealed(key);
      notify("API Key 已创建", "success");
      await refresh();
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string | number, name: string) {
    if (!window.confirm(`确认删除 API Key“${name}”？使用它的客户端将立即失去访问权限。`)) return;
    try {
      await api.deleteApiKey(id);
      notify("API Key 已删除", "success");
      await refresh();
    } catch (caught) {
      notify(displayError(caught), "error");
    }
  }

  async function copy(value: string) {
    await navigator.clipboard.writeText(value);
    notify("已复制到剪贴板", "success");
  }

  return (
    <div className="page-stack">
      <PageHeader title="API Keys" description="管理视频创建、查询权限和调用配额" actions={<><button className="button button-secondary" onClick={() => void refresh()}><RefreshCw size={16} />刷新</button><button className="button button-primary" onClick={() => setCreateOpen(true)}><Plus size={16} />创建 Key</button></>} />
      <section className="panel panel-flush">
        {loading && !data ? <LoadingState /> : error && !data ? <ErrorState error={error} onRetry={refresh} /> : !keys.length ? <EmptyState label="还没有 API Key" /> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>名称 / Key</th><th>状态</th><th>Scopes</th><th>限速与配额</th><th>调用量</th><th>最近使用</th><th className="actions-cell">操作</th></tr></thead>
              <tbody>{keys.map((key) => (
                <tr key={key.id}>
                  <td><div className="primary-cell"><strong>{key.name}</strong><code>{key.key_preview || key.key || `key_${key.id}`}</code></div></td>
                  <td><StatusBadge value={key.is_active === false ? "DISABLED" : "ACTIVE"} /></td>
                  <td><div className="scope-list">{(key.scopes || []).map((scope) => <span key={scope}>{scope}</span>)}</div></td>
                  <td><div className="primary-cell"><span>{key.rate_limit_per_minute ? `${formatNumber(key.rate_limit_per_minute)} / min` : "无限速"}</span><small>日 {key.daily_quota ? formatNumber(key.daily_quota) : "-"} · 月 {key.monthly_quota ? formatNumber(key.monthly_quota) : "-"}</small></div></td>
                  <td><div className="primary-cell"><strong>{formatNumber(key.total_calls)}</strong><small>日 {formatNumber(key.daily_calls)} · 月 {formatNumber(key.monthly_calls)}</small></div></td>
                  <td>{formatDate(key.last_used_at)}</td>
                  <td className="actions-cell"><button className="icon-button icon-button-danger" title="删除 Key" aria-label="删除 Key" onClick={() => void remove(key.id, key.name)}><Trash2 size={17} /></button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="创建 API Key" description="密钥正文仅在创建成功后显示一次。" width="small">
        <form onSubmit={create} className="form-stack">
          <label className="field"><span>名称</span><input name="name" placeholder="Production client" required autoFocus /></label>
          <fieldset className="field-group"><legend>Scopes</legend>{scopeOptions.map((scope) => <label className="check-field" key={scope}><input type="checkbox" name="scopes" value={scope} defaultChecked /><span>{scope}</span></label>)}</fieldset>
          <label className="field"><span>每分钟请求数</span><input name="rate_limit_per_minute" type="number" min={1} placeholder="留空为默认值" /></label>
          <div className="form-grid"><label className="field"><span>每日配额</span><input name="daily_quota" type="number" min={1} /></label><label className="field"><span>每月配额</span><input name="monthly_quota" type="number" min={1} /></label></div>
          <div className="modal-actions"><button type="button" className="button button-secondary" onClick={() => setCreateOpen(false)}>取消</button><button type="submit" className="button button-primary" disabled={busy}><KeyRound size={16} />创建</button></div>
        </form>
      </Modal>

      <Modal open={Boolean(revealed)} onClose={() => setRevealed(null)} title="保存新 API Key" description="关闭后无法再次查看完整密钥。" width="small">
        {revealed && <div className="reveal-key"><code>{revealed}</code><button className="button button-primary" onClick={() => void copy(revealed)}><Copy size={16} />复制密钥</button></div>}
      </Modal>
    </div>
  );
}

function optionalNumber(value: FormDataEntryValue | null): number | null {
  const text = String(value || "").trim();
  return text ? Number(text) : null;
}
